import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSubmitCompanyRegistrationMutation } from "@/state/submissionsApi";
import RwandaCountry from '@/assets/images/pillar-rwanda.jpg';
import KenyaCountry from '@/assets/images/pillar-kenya.jpg';
import TanzaniaCountry from '@/assets/images/pillar-tanzania.jpg';
import UgandaCountry from '@/assets/images/pillar-uganda.jpg';
import EthiopieCountry from '@/assets/images/pillar-ethiopia.jpg';
import BurundiCountry from '@/assets/images/pillar-burundi.jpg';
import CongoCountry from '@/assets/images/pillar-congo.jpg';

const steps = [
  { icon: "ri-file-list-3-line", title: "Document Prep", desc: "We collect and verify all required incorporation documents." },
  { icon: "ri-government-line", title: "Name Reservation", desc: "Reserve your unique company name with the relevant authority." },
  { icon: "ri-shield-check-line", title: "Filing & Approval", desc: "We submit your application and manage communication with regulators." },
  { icon: "ri-award-line", title: "Certificate Issued", desc: "Receive your Certificate of Incorporation and start trading." },
];

const countries = [
  { name: "Rwanda", flag: "🇷🇼", time: "3–5 days", note: "100% foreign ownership allowed", img: RwandaCountry},
  { name: "Kenya", flag: "🇰🇪", time: "5–7 days", note: "Largest economy in East Africa", img: KenyaCountry },
  { name: "Tanzania", flag: "🇹🇿", time: "7–10 days", note: "Strategic access to SADC markets", img: TanzaniaCountry },
  { name: "Uganda", flag: "🇺🇬", time: "5–7 days", note: "Gateway to South Sudan & DRC", img: UgandaCountry},
  { name: "Ethiopia", flag: "🇪🇹", time: "10–14 days", note: "Largest population in the region", img: EthiopieCountry },
  { name: "Burundi", flag: "🇧🇮", time: "7–10 days", note: "EAC member, growing economy", img: BurundiCountry },
  { name: "DRC", flag: "🇨🇩", time: "14–21 days", note: "Vast resources & consumer market", img: CongoCountry },
];

const CompanyRegistrationSchema = z.object({
  name: z.string().min(1, "Full name is required"),
  email: z.email("Invalid email address"),
  country: z.string().min(1, "Country is required"),
  sector: z.string().min(1, "Sector is required"),
  activity: z.string().max(500).optional(),
});

type CompanyRegistrationFormValues = z.infer<typeof CompanyRegistrationSchema>;

export default function CompanyRegistration() {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitCompanyRegistration, { isLoading }] = useSubmitCompanyRegistrationMutation();

  const { register, handleSubmit, formState: { errors }, watch, reset } = useForm<CompanyRegistrationFormValues>({
    resolver: zodResolver(CompanyRegistrationSchema),
    defaultValues: { name: "", email: "", country: "", sector: "", activity: "" },
  });

  const activityLength = watch("activity")?.length ?? 0;

  const onSubmit = async (data: CompanyRegistrationFormValues) => {
    setSubmitError("");
    try {
      await submitCompanyRegistration({
        name: data.name,
        email: data.email,
        country: data.country,
        sector: data.sector,
        activity: data.activity || undefined,
      }).unwrap();
      setSubmitted(true);
      reset();
    } catch (err) {
      const message = (err as any)?.data?.error?.message;
      setSubmitError(message ?? "Something went wrong. Please try again.");
    }
  };

  return (
    <section id="registration" className="py-24 bg-white">
      <div className="w-full px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-16">
          <div>
            <p className="text-[#00C2C2] text-xs font-semibold tracking-widest uppercase mb-3">Section A</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0A1628] leading-tight">
              Company <span className="text-[#00C2C2]">Registration</span>
            </h2>
            <p className="text-gray-500 text-base mt-4 max-w-xl leading-relaxed">
              Fast, hassle-free incorporation across Rwanda and East Africa. We handle everything from paperwork to approval.
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0A1628]/10 text-[#00C2C2]">
              <i className="ri-time-line" />
            </div>
            <span>Rwanda: as fast as <strong className="text-[#0A1628]">3 days</strong></span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 mb-16">
          {countries.map((c) => (
            <div key={c.name} className="rounded-lg border border-gray-100 hover:border-[#00C2C2]/40 transition-all group cursor-default overflow-hidden">
              <div className="w-full h-24 overflow-hidden">
                <img src={c.img} alt={c.name} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-3 bg-white">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-lg">{c.flag}</span>
                  <p className="text-sm font-bold text-[#0A1628] group-hover:text-[#00C2C2] transition-colors">{c.name}</p>
                </div>
                <p className="text-xs text-[#00C2C2] font-medium">{c.time}</p>
                <p className="text-xs text-gray-400 mt-0.5 leading-snug">{c.note}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h3 className="text-xl font-bold text-[#0A1628] mb-8">How It Works</h3>
            <div className="relative">
              <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-gray-100" />
              <div className="flex flex-col gap-8">
                {steps.map((step, i) => (
                  <div key={step.title} className="relative flex gap-5 items-start">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0A1628] text-white shrink-0 z-10">
                      <i className={`${step.icon} text-base`} />
                    </div>
                    <div className="pt-1.5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-[#00C2C2] tracking-widest uppercase">Step {i + 1}</span>
                      </div>
                      <p className="font-bold text-[#0A1628] text-sm mb-1">{step.title}</p>
                      <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10 bg-[#0A1628] rounded-lg p-6 text-white">
              <p className="text-[#00C2C2] text-xs font-semibold tracking-widest uppercase mb-2">Why Rwanda?</p>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Rwanda ranks <strong className="text-white">#2 in Africa</strong> for ease of doing business. 100% foreign ownership, no minimum capital requirement, and digital-first registration make it the top entry point for East Africa.
              </p>
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                {[["3 Days", "Avg. Registration"], ["0%", "Min. Capital"], ["100%", "Foreign Ownership"]].map(([val, lbl]) => (
                  <div key={lbl}>
                    <p className="text-xl font-bold text-[#00C2C2]">{val}</p>
                    <p className="text-white/50 text-xs mt-0.5">{lbl}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-[#0A1628] mb-8">Start Your Registration</h3>
            {submitted ? (
              <div className="bg-[#0A1628]/8 border border-[#0A1628]/30 rounded-lg p-10 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#0A1628] text-white text-2xl mx-auto mb-4">
                  <i className="ri-check-line" />
                </div>
                <h4 className="text-lg font-bold text-[#0A1628] mb-2">Application Received!</h4>
                <p className="text-gray-500 text-sm">Our team will reach out within 24 hours to guide your next steps.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-[#00C2C2] text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap"
                >
                  Submit another application
                </button>
              </div>
            ) : (
              <form id="company-registration-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                {submitError && (
                  <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{submitError}</p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#0A1628] tracking-wide uppercase">Full Name *</label>
                    <input
                      type="text"
                      placeholder="John Doe"
                      {...register("name")}
                      className="text-sm border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#00C2C2] bg-[#f8f9fc] text-[#0A1628] placeholder-gray-400 transition-colors"
                    />
                    {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#0A1628] tracking-wide uppercase">Email Address *</label>
                    <input
                      type="email"
                      placeholder="john@company.com"
                      {...register("email")}
                      className="text-sm border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#00C2C2] bg-[#f8f9fc] text-[#0A1628] placeholder-gray-400 transition-colors"
                    />
                    {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#0A1628] tracking-wide uppercase">Country of Registration *</label>
                  <select
                    {...register("country")}
                    className="text-sm border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#00C2C2] bg-[#f8f9fc] text-[#0A1628] transition-colors cursor-pointer"
                  >
                    <option value="">Select a country</option>
                    <option value="Rwanda">Rwanda</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Tanzania">Tanzania</option>
                    <option value="Uganda">Uganda</option>
                    <option value="Ethiopia">Ethiopia</option>
                    <option value="DRC">DRC</option>
                    <option value="Burundi">Burundi</option>
                  </select>
                  {errors.country && <p className="text-xs text-red-500">{errors.country.message}</p>}
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#0A1628] tracking-wide uppercase">Industry Sector *</label>
                  <select
                    {...register("sector")}
                    className="text-sm border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#00C2C2] bg-[#f8f9fc] text-[#0A1628] transition-colors cursor-pointer"
                  >
                    <option value="">Select a sector</option>
                    <option value="Technology">Technology</option>
                    <option value="Finance">Finance</option>
                    <option value="Agriculture">Agriculture</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Energy">Energy</option>
                    <option value="Logistics">Logistics</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Tourism">Tourism</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.sector && <p className="text-xs text-red-500">{errors.sector.message}</p>}
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#0A1628] tracking-wide uppercase">Business Activity</label>
                  <textarea
                    rows={3}
                    maxLength={500}
                    placeholder="Briefly describe what your business does..."
                    {...register("activity")}
                    className="text-sm border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#00C2C2] bg-[#f8f9fc] text-[#0A1628] placeholder-gray-400 transition-colors resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right">{activityLength}/500</p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-[#0A1628] text-white text-sm font-bold px-6 py-3.5 rounded-lg hover:bg-[#0A1628] transition-all whitespace-nowrap disabled:opacity-60 cursor-pointer flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <><i className="ri-loader-4-line animate-spin" /> Processing...</>
                  ) : (
                    <><i className="ri-arrow-right-line" /> Start Now</>
                  )}
                </button>
                <p className="text-xs text-gray-400 text-center">We respond within 24 hours. Your information is confidential.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
